/**
 * SkinScreeningPage — iter261 Lot D.
 *
 * Educational skin-lesion ABCDE feedback. CE-SAFE — does NOT diagnose.
 *
 * Flow:
 *   1. Disclaimer + GDPR special-category consent (Article 9) — TWO checkboxes
 *      that MUST both be checked before upload becomes possible.
 *   2. Optional storage consent — only if checked do we keep the file.
 *   3. Image upload → /api/skin-lesion/analyze
 *   4. Render ABCDE assessment + book dermatologist CTA.
 *
 * Strict positioning: "discuss these areas with your dermatologist" — never
 * "you have melanoma" or "this is benign". Identical compliance posture to
 * the DentalXrayFeedbackPage.
 */
import React, { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { Button } from "@/components/ui/button";
import CreditBadge from "@/components/CreditBadge";  // iter1118
import {
  ShieldCheck, Upload, AlertTriangle, ArrowLeft, ExternalLink,
  Trash2, CheckCircle2, BookOpen, Heart, ListChecks, EyeOff, Sparkles,
  Clock, Calendar, AlarmClock, Siren,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_BYTES = 8 * 1024 * 1024;
const ALLOWED_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];

// ABCDE category labels
const ABCDE_LABELS = {
  asymmetry: { bg: "A — Асиметрия", en: "A — Asymmetry" },
  border:    { bg: "B — Граници",   en: "B — Border" },
  color:     { bg: "C — Цвят",      en: "C — Color" },
  diameter:  { bg: "D — Диаметър",  en: "D — Diameter" },
  evolution: { bg: "E — Еволюция",  en: "E — Evolution" },
};

export default function SkinScreeningPage() {
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";

  const [consentEdu, setConsentEdu] = useState(false);
  const [consentData, setConsentData] = useState(false);
  const [consentStorage, setConsentStorage] = useState(false);
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const canSubmit = consentEdu && consentData && file && !loading;

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!ALLOWED_TYPES.includes(f.type)) {
      toast.error(bg ? "Само JPG, PNG или WEBP" : "Only JPG, PNG or WEBP");
      return;
    }
    if (f.size > MAX_BYTES) {
      toast.error(bg ? "Файлът е твърде голям (макс. 8 MB)" : "File too large (max 8 MB)");
      return;
    }
    setFile(f);
    setPreviewUrl(URL.createObjectURL(f));
  };

  const submit = async () => {
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("image", file);
      fd.append("language", bg ? "bg" : "en");
      fd.append("consent_educational_use", "true");
      fd.append("consent_data_processing", "true");
      fd.append("consent_allow_storage", consentStorage ? "true" : "false");
      const { data } = await axios.post(`${API}/skin-lesion/analyze`, fd);
      setResult(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || (bg ? "Грешка" : "Error"));
    } finally {
      setLoading(false);
    }
  };

  const deleteScreening = async () => {
    if (!result?.screening_id) return;
    try {
      await axios.delete(`${API}/skin-lesion/screening/${result.screening_id}`);
      toast.success(bg ? "Изтрито" : "Deleted");
      reset();
    } catch (e) {
      toast.error(bg ? "Грешка при изтриване" : "Delete failed");
    }
  };

  const reset = () => {
    setResult(null);
    setFile(null);
    setPreviewUrl(null);
    setConsentEdu(false);
    setConsentData(false);
    setConsentStorage(false);
  };

  const cardCls = isDark
    ? "bg-[hsl(223,47%,11%)] border border-white/5"
    : "bg-white border border-gray-100 shadow-sm";

  // Concern level → visual style mapping (shared logic for ABCDE rows)
  const concernStyle = (level) => {
    if (level === "attention_needed") return "border-red-500/40 bg-red-500/5";
    if (level === "moderate")          return "border-orange-400/40 bg-orange-400/5";
    if (level === "low")               return "border-amber-400/40 bg-amber-400/5";
    return "border-emerald-400/30 bg-emerald-400/5";
  };
  const concernChip = (level) => {
    if (level === "attention_needed") return "text-red-700 dark:text-red-300 bg-red-500/15";
    if (level === "moderate")          return "text-orange-700 dark:text-orange-300 bg-orange-500/15";
    if (level === "low")               return "text-amber-700 dark:text-amber-300 bg-amber-500/15";
    return "text-emerald-700 dark:text-emerald-300 bg-emerald-500/15";
  };
  const concernLabel = (level) => {
    if (level === "attention_needed") return bg ? "Нужно внимание" : "Attention";
    if (level === "moderate")          return bg ? "Умерено" : "Moderate";
    if (level === "low")               return bg ? "Леко" : "Low";
    return bg ? "Без притеснение" : "No concern";
  };

  // Urgency band → visual triage (colour + icon + timeframe label)
  const urgencyConfig = {
    routine_yearly: {
      icon: Calendar,
      timeframeBg: "Рутинен годишен преглед",
      timeframeEn: "Routine yearly check-up",
      ringClass: "border-emerald-400 bg-emerald-50/40 dark:bg-emerald-900/15",
      iconClass: "text-emerald-600 dark:text-emerald-300",
      titleClass: "text-emerald-700 dark:text-emerald-300",
      ctaText: { bg: "Запази час за профилактичен преглед", en: "Book a routine appointment" },
    },
    within_3_months: {
      icon: Clock,
      timeframeBg: "В следващите 3 месеца",
      timeframeEn: "Within the next 3 months",
      ringClass: "border-amber-400 bg-amber-50/40 dark:bg-amber-900/15",
      iconClass: "text-amber-600 dark:text-amber-300",
      titleClass: "text-amber-700 dark:text-amber-300",
      ctaText: { bg: "Намери дерматолог за следващите 3 месеца", en: "Find a dermatologist (within 3 months)" },
    },
    within_1_month: {
      icon: AlarmClock,
      timeframeBg: "В следващия 1 месец",
      timeframeEn: "Within the next month",
      ringClass: "border-orange-400 bg-orange-50/40 dark:bg-orange-900/15",
      iconClass: "text-orange-600 dark:text-orange-300",
      titleClass: "text-orange-700 dark:text-orange-300",
      ctaText: { bg: "Намери дерматолог в близък час", en: "Find a dermatologist soon" },
    },
    within_2_weeks: {
      icon: Siren,
      timeframeBg: "В следващите 2 седмици",
      timeframeEn: "Within the next 2 weeks",
      ringClass: "border-red-500 bg-red-50/40 dark:bg-red-900/20",
      iconClass: "text-red-600 dark:text-red-300",
      titleClass: "text-red-700 dark:text-red-300",
      ctaText: { bg: "Запази час при дерматолог СЕГА", en: "Book a dermatologist NOW" },
    },
  };
  const getUrgencyConfig = (band) => urgencyConfig[band] || urgencyConfig.within_3_months;

  return (
    <div className={isDark ? "min-h-screen bg-[hsl(223,47%,7%)] text-gray-100" : "min-h-screen bg-[#F8FAFC] text-gray-900"}>
      <div className="max-w-3xl mx-auto px-4 py-8 sm:py-12">
        {/* Brand */}
        <Link to="/" className="inline-block mb-6">
          <img src="/app-icon.png" alt="Nutramilo" className="h-9 w-9 object-contain sm:hidden" />
          <img src="/nutramilo-logo.png" alt="Nutramilo" className="hidden sm:block h-12 w-auto object-contain" onError={(e) => { e.target.style.display = "none"; }} />
        </Link>

        {/* Top nav */}
        <div className="flex flex-wrap items-center gap-2 mb-6 text-xs">
          <Link
            to="/"
            data-testid="skin-back-home"
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full transition-colors
              ${isDark ? "bg-white/5 hover:bg-white/10 text-gray-300" : "bg-white border border-gray-200 hover:border-gray-300 text-gray-700"}`}
          >
            <ArrowLeft className="w-3.5 h-3.5" /> {bg ? "Към началото" : "Back home"}
          </Link>
        </div>

        {/* Hero */}
        <header className="mb-8">
          <div className="flex items-center gap-2 mb-3 text-xs uppercase tracking-wider opacity-70">
            <BookOpen className="w-4 h-4 text-[#F472B6]" />
            <span>{bg ? "Образователен инструмент — Фаза 2 прототип" : "Educational tool — Phase 2 prototype"}</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-3">
            {bg ? "Образователна оценка на кожни лезии" : "Skin lesion educational feedback"}
          </h1>
          <p className={`text-base ${isDark ? "text-gray-400" : "text-gray-600"}`}>
            {bg
              ? "Качете снимка на бенка или петно и получете ABCDE наблюдения за обсъждане при следваща визита при дерматолог. Този инструмент НЕ поставя диагноза."
              : "Upload a photo of a mole or spot and receive ABCDE observations for discussion at your next dermatology visit. This tool does NOT diagnose."}
          </p>
        </header>

        {/* Disclaimer banner */}
        <div className="mb-6 p-4 rounded-2xl border-2 border-amber-400 bg-amber-50/30 dark:bg-amber-900/10">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <div className="font-semibold mb-1 text-amber-700 dark:text-amber-400">
                {bg ? "Този инструмент НЕ е медицинско изделие" : "This tool is NOT a medical device"}
              </div>
              <p className={isDark ? "text-amber-200/80" : "text-amber-900/80"}>
                {bg
                  ? "Не открива меланом, не интерпретира клинични находки и не поставя диагнози. Винаги се консултирайте с дерматолог за решения относно лечение."
                  : "It does not detect melanoma, interpret clinical findings or make diagnoses. Always consult a dermatologist for treatment decisions."}
              </p>
            </div>
          </div>
        </div>

        {!result && (
          <>
            {/* Consent block */}
            <div className={`${cardCls} rounded-2xl p-5 sm:p-6 mb-5`}>
              <h2 className="text-sm font-semibold mb-3 flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-[#F472B6]" />
                {bg ? "Необходими съгласия (GDPR Член 9)" : "Required consents (GDPR Article 9)"}
              </h2>
              <div className="space-y-2.5">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={consentEdu}
                    onChange={(e) => setConsentEdu(e.target.checked)}
                    className="mt-0.5"
                    data-testid="skin-consent-edu"
                  />
                  <span className="text-sm">
                    {bg
                      ? "Разбирам, че този инструмент е образователен и не поставя медицински диагнози."
                      : "I understand this tool is educational and does not make medical diagnoses."}
                  </span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={consentData}
                    onChange={(e) => setConsentData(e.target.checked)}
                    className="mt-0.5"
                    data-testid="skin-consent-data"
                  />
                  <span className="text-sm">
                    {bg
                      ? "Съгласен/на съм Nutramilo да обработи качената от мен снимка на кожна лезия с цел генериране на образователна обратна връзка."
                      : "I consent to Nutramilo processing my uploaded skin lesion photo to generate educational feedback."}
                  </span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={consentStorage}
                    onChange={(e) => setConsentStorage(e.target.checked)}
                    className="mt-0.5"
                    data-testid="skin-consent-storage"
                  />
                  <span className="text-sm">
                    {bg
                      ? "(Незадължително) Съгласен/на съм Nutramilo да съхрани снимката, за да я виждам в моя профил. По всяко време мога да поискам изтриване."
                      : "(Optional) I consent to Nutramilo storing the image so I can view it in my account. I can request deletion at any time."}
                  </span>
                </label>
              </div>
            </div>

            {/* Upload */}
            <div className={`${cardCls} rounded-2xl p-5 sm:p-6 mb-5`}>
              <h2 className="text-sm font-semibold mb-3">
                {bg ? "Качете снимка на лезията" : "Upload lesion photo"}
              </h2>
              <label
                htmlFor="skin-file"
                className={`flex flex-col items-center gap-3 p-8 rounded-xl border-2 border-dashed cursor-pointer transition-colors
                  ${(consentEdu && consentData)
                    ? (isDark ? "border-[#F472B6]/50 hover:border-[#F472B6]" : "border-[#F472B6]/40 hover:border-[#F472B6]")
                    : "border-gray-300 opacity-50 cursor-not-allowed"}`}
              >
                {previewUrl ? (
                  <img src={previewUrl} alt="Skin lesion preview" className="max-h-48 rounded-lg" />
                ) : (
                  <>
                    <Upload className="w-8 h-8 opacity-50" />
                    <span className="text-sm">{bg ? "JPG, PNG или WEBP — макс. 8 MB" : "JPG, PNG or WEBP — max 8 MB"}</span>
                    <span className={`text-xs ${isDark ? "text-gray-500" : "text-gray-400"}`}>
                      {bg ? "Съвет: близка снимка под добра светлина" : "Tip: close-up under good lighting"}
                    </span>
                  </>
                )}
                <input
                  id="skin-file"
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  disabled={!(consentEdu && consentData)}
                  onChange={handleFile}
                  data-testid="skin-file-input"
                />
              </label>
              {file && (
                <p className={`text-xs mt-3 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  {file.name} · {Math.round(file.size / 1024)} KB
                </p>
              )}
            </div>

            <div className="mb-2 flex justify-center">
              <CreditBadge featureId="medical_image_analysis" fallbackCost={1} />
            </div>
            <Button
              data-testid="skin-submit-btn"
              onClick={submit}
              disabled={!canSubmit}
              className="w-full bg-gradient-to-r from-[#F472B6] to-[#A855F7] text-white py-6 text-base rounded-full"
            >
              {loading
                ? (bg ? "Обработване…" : "Processing…")
                : (bg ? "Получи ABCDE обратна връзка" : "Get ABCDE feedback")}
            </Button>
          </>
        )}

        {result && (
          <div className="space-y-5" data-testid="skin-result">
            {/* URGENCY TRIAGE BANNER — most important block, top of results */}
            {result.urgency_band && result.feedback_type !== "vision_unknown" && (() => {
              const cfg = getUrgencyConfig(result.urgency_band);
              const UrgencyIcon = cfg.icon;
              return (
                <div
                  className={`p-5 sm:p-6 rounded-2xl border-2 ${cfg.ringClass}`}
                  data-testid="skin-urgency-banner"
                  data-urgency-band={result.urgency_band}
                >
                  <div className="flex items-start gap-4">
                    <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center bg-white/60 dark:bg-white/10 ${cfg.iconClass}`}>
                      <UrgencyIcon className="w-6 h-6" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className={`text-[11px] uppercase tracking-wider font-semibold mb-1 ${cfg.titleClass}`}>
                        {bg ? "Препоръчителен срок за дерматолог" : "Recommended dermatologist timeframe"}
                      </div>
                      <div className={`text-xl sm:text-2xl font-bold mb-2 ${cfg.titleClass}`} data-testid="skin-urgency-timeframe">
                        {bg ? cfg.timeframeBg : cfg.timeframeEn}
                      </div>
                      {(bg ? result.urgency_reason_bg : result.urgency_reason_en) && (
                        <p className={`text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`} data-testid="skin-urgency-reason">
                          {bg ? result.urgency_reason_bg : result.urgency_reason_en}
                        </p>
                      )}
                      <a
                        href="https://superdoc.bg/lekari/dermatologiya"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="skin-urgency-cta"
                        className={`inline-flex items-center gap-2 mt-3 px-4 py-2 rounded-full text-sm font-semibold
                          ${result.urgency_band === "within_2_weeks"
                            ? "bg-red-600 hover:bg-red-700 text-white"
                            : result.urgency_band === "within_1_month"
                            ? "bg-orange-500 hover:bg-orange-600 text-white"
                            : result.urgency_band === "within_3_months"
                            ? "bg-amber-500 hover:bg-amber-600 text-white"
                            : "bg-emerald-500 hover:bg-emerald-600 text-white"}`}
                      >
                        {bg ? cfg.ctaText.bg : cfg.ctaText.en}
                        <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Image type / quality banner */}
            {result.feedback_type === "vision_unknown" && (
              <div className="p-4 rounded-2xl border-2 border-orange-400 bg-orange-50/30 dark:bg-orange-900/10">
                <div className="text-sm font-semibold mb-1 text-orange-700 dark:text-orange-400">
                  {bg ? "Това не изглежда като снимка на кожна лезия" : "This doesn't look like a skin lesion photo"}
                </div>
                <p className={isDark ? "text-orange-200/80" : "text-orange-900/80"}>
                  {bg
                    ? "Опитайте с по-близка снимка под добра светлина, която ясно показва бенката или петното."
                    : "Try a closer photo under good lighting that clearly shows the mole or spot."}
                </p>
              </div>
            )}

            {result.image_type === "skin_lesion" && (
              <div className={`${cardCls} rounded-2xl p-4 sm:p-5 space-y-3`}>
                <div className="flex flex-wrap items-center gap-3">
                  <span className="px-2 py-1 rounded-full text-[11px] uppercase tracking-wider bg-[#F472B6]/15 text-[#F472B6]">
                    {bg ? "Кожна лезия" : "Skin lesion"}
                  </span>
                  {result.image_quality_rating && (
                    <span className={`px-2 py-1 rounded-full text-[11px] uppercase tracking-wider
                      ${result.image_quality_rating === "good" ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300" :
                        result.image_quality_rating === "poor" ? "bg-orange-500/15 text-orange-700 dark:text-orange-300" :
                        "bg-amber-500/15 text-amber-700 dark:text-amber-300"}`}>
                      {bg ? "Качество: " : "Quality: "}{result.image_quality_rating}
                    </span>
                  )}
                  {result.overall_concern && (
                    <span className={`px-2 py-1 rounded-full text-[11px] uppercase tracking-wider ${concernChip(result.overall_concern)}`}>
                      {bg ? "Общо: " : "Overall: "}{concernLabel(result.overall_concern)}
                    </span>
                  )}
                  {(bg ? result.image_quality_bg : result.image_quality_en) && (
                    <span className={`text-xs flex-1 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                      {bg ? result.image_quality_bg : result.image_quality_en}
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* ABCDE Assessment */}
            {result.abcde_assessment && Object.keys(result.abcde_assessment).length > 0 && (
              <div className={`${cardCls} rounded-2xl p-5 sm:p-6 border-l-4 border-[#F472B6]`}>
                <h2 className="text-base font-semibold mb-4 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-[#F472B6]" />
                  {bg ? "ABCDE наблюдения" : "ABCDE observations"}
                </h2>
                <ul className="space-y-3">
                  {["asymmetry", "border", "color", "diameter", "evolution"].map((key) => {
                    const item = result.abcde_assessment[key];
                    if (!item) return null;
                    const level = item.concern_level || "none";
                    const obs = bg ? item.observation_bg : item.observation_en;
                    return (
                      <li key={`abcde-${key}`} className={`p-3 rounded-xl border ${concernStyle(level)}`} data-testid={`skin-abcde-${key}`}>
                        <div className="flex flex-wrap items-start gap-2 mb-1.5">
                          <span className={`text-[11px] px-2 py-0.5 rounded-full uppercase tracking-wider ${concernChip(level)}`}>
                            {concernLabel(level)}
                          </span>
                          <span className="text-xs uppercase tracking-wider opacity-70 font-semibold">
                            {bg ? ABCDE_LABELS[key].bg : ABCDE_LABELS[key].en}
                          </span>
                        </div>
                        <div className={`text-sm ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                          {obs}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            {/* Additional observations */}
            {(bg ? result.additional_observations_bg : result.additional_observations_en)?.length > 0 && (
              <div className={`${cardCls} rounded-2xl p-5 sm:p-6 border-l-4 border-[#A855F7]`}>
                <h2 className="text-base font-semibold mb-3 flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-[#A855F7]" />
                  {bg ? "Допълнителни наблюдения" : "Additional observations"}
                </h2>
                <ul className={`space-y-2 text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  {(bg ? result.additional_observations_bg : result.additional_observations_en).map((p, i) => (
                    <li key={`addl-${i}-${String(p).slice(0, 30)}`} className="flex items-start gap-2">
                      <span className="mt-1 w-1.5 h-1.5 rounded-full bg-[#A855F7] flex-shrink-0" />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Reassuring points */}
            {(bg ? result.reassuring_points_bg : result.reassuring_points_en)?.length > 0 && (
              <div className={`${cardCls} rounded-2xl p-5 sm:p-6 border-l-4 border-emerald-400`}>
                <h2 className="text-base font-semibold mb-3 flex items-center gap-2 text-emerald-700 dark:text-emerald-300">
                  <Heart className="w-5 h-5" />
                  {bg ? "Какво е наред" : "What looks fine"}
                </h2>
                <ul className={`space-y-2 text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  {(bg ? result.reassuring_points_bg : result.reassuring_points_en).map((p, i) => (
                    <li key={`reassure-${i}-${String(p).slice(0, 30)}`} className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 mt-0.5 text-emerald-500 flex-shrink-0" />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Suggested next steps */}
            {(bg ? result.suggested_next_steps_bg : result.suggested_next_steps_en)?.length > 0 && (
              <div className={`${cardCls} rounded-2xl p-5 sm:p-6 border-l-4 border-[#6366F1]`}>
                <h2 className="text-base font-semibold mb-3 flex items-center gap-2">
                  <ListChecks className="w-5 h-5 text-[#6366F1]" />
                  {bg ? "Препоръчани стъпки" : "Recommended next steps"}
                </h2>
                <ul className={`space-y-2 text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  {(bg ? result.suggested_next_steps_bg : result.suggested_next_steps_en).map((s, i) => (
                    <li key={`step-${i}-${String(s).slice(0, 30)}`} className="flex items-start gap-2">
                      <span className="mt-1 w-1.5 h-1.5 rounded-full bg-[#6366F1] flex-shrink-0" />
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Limitations */}
            {(bg ? result.limitations_bg : result.limitations_en) && (
              <div className={`${cardCls} rounded-2xl p-4 sm:p-5 flex items-start gap-3`}>
                <EyeOff className={`w-4 h-4 mt-0.5 flex-shrink-0 ${isDark ? "text-gray-500" : "text-gray-400"}`} />
                <div className={`text-xs ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                  <div className="font-semibold mb-1 uppercase tracking-wider opacity-80">
                    {bg ? "Ограничения на анализа" : "Analysis limitations"}
                  </div>
                  {bg ? result.limitations_bg : result.limitations_en}
                </div>
              </div>
            )}

            {/* Disclaimer */}
            <div className="p-4 rounded-2xl border-2 border-amber-400 bg-amber-50/30 dark:bg-amber-900/10">
              <div className="flex items-start gap-3 text-sm">
                <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className={isDark ? "text-amber-200/80" : "text-amber-900/80"}>
                  {bg ? result.disclaimer_bg : result.disclaimer_en}
                </div>
              </div>
            </div>

            {/* Book dermatologist — Superdoc deeplink */}
            <a
              href="https://superdoc.bg/lekari/dermatologiya"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="skin-book-dermatologist"
              className="flex flex-col sm:flex-row items-center justify-center gap-2 w-full px-5 py-4 rounded-2xl bg-gradient-to-r from-[#F472B6] to-[#A855F7] text-white text-sm font-medium hover:opacity-90 transition-opacity shadow-lg"
            >
              <div className="flex flex-col items-center sm:items-start">
                <span className="text-[11px] uppercase tracking-wider opacity-90 mb-0.5">
                  {bg ? "Запази час" : "Book appointment"}
                </span>
                <span className="text-base font-semibold">
                  {bg ? "Намери дерматолог в Superdoc" : "Find a dermatologist on Superdoc"}
                </span>
              </div>
              <ExternalLink className="w-4 h-4 sm:ml-auto flex-shrink-0" />
            </a>

            <div className="flex gap-2">
              <Button
                variant="ghost"
                onClick={reset}
                className="flex-1 text-sm opacity-70 hover:opacity-100"
                data-testid="skin-restart-btn"
              >
                {bg ? "Качи нова снимка" : "Upload new image"}
              </Button>
              {result.screening_id && (
                <Button
                  variant="ghost"
                  onClick={deleteScreening}
                  className="flex items-center gap-2 text-sm text-red-500 hover:text-red-600"
                  data-testid="skin-delete-btn"
                >
                  <Trash2 className="w-4 h-4" />
                  {bg ? "Изтрий моите данни" : "Delete my data"}
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
